import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  MapPin,
  Package,
  Search,
  Send,
  Truck,
} from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';
import { trackOrder } from '../lib/orderTracking';

const supportRows = [
  {
    icon: Mail,
    label: 'Email',
    value: 'sales@berleto.com',
    detail: 'Response within 24 hours',
  },
  {
    icon: Clock,
    label: 'Hours',
    value: 'Monday to Friday, 9am to 6pm PST',
    detail: 'Saturday support: 10am to 4pm PST',
  },
  {
    icon: MapPin,
    label: 'Ships from',
    value: 'California, USA',
    detail: 'Most orders leave within 24 hours',
  },
];

const quickLinks = [
  { label: 'Return policy', href: '/faq' },
  { label: 'Shipping details', href: '/shipping' },
  { label: 'Subscription help', href: '/faq' },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium tracking-[0.18em] uppercase mb-3" style={{ color: 'rgba(26,42,26,0.42)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const fieldClass =
  'w-full border bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-[#B8704D]';

export function ContactPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const [activeTab, setActiveTab] = useState('contact');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  const formatStatus = (status) => {
    const statusMap = {
      processing: 'Processing',
      shipped: 'Shipped',
      in_transit: 'In Transit',
      out_for_delivery: 'Out for Delivery',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
    };
    return statusMap[status] || status;
  };

  const formatDeliveryDate = (date) => {
    if (!date) return 'Calculating...';
    const deliveryDate = new Date(date);
    const today = new Date();
    const diffDays = Math.ceil((deliveryDate - today) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays > 0 && diffDays <= 5) return `${diffDays} business days`;
    return deliveryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatEventDate = (date) => {
    const eventDate = new Date(date);
    const now = new Date();
    const diffHours = Math.floor((now - eventDate) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffHours < 48) return 'Yesterday';
    return eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleTrackingSubmit = async (e) => {
    e.preventDefault();
    setIsTracking(true);
    setTrackingError(null);
    setTrackingResult(null);

    if (trackingNumber) {
      try {
        const result = await trackOrder(trackingNumber);

        if (result.error) {
          setTrackingError(result.error);
        } else {
          setTrackingResult({
            number: result.order.tracking_number,
            status: formatStatus(result.order.status),
            lastUpdate: result.events[0]?.description || 'Order is being processed',
            location: result.events[0]?.location || 'California, USA',
            estimatedDelivery: formatDeliveryDate(result.order.estimated_delivery),
            updates: result.events.map((event) => ({
              date: formatEventDate(event.created_at),
              status: event.description,
              location: event.location,
            })),
          });
        }
      } catch (error) {
        console.error('Tracking error:', error);
        setTrackingError('Unable to track order. Please try again later.');
      }
    }

    setIsTracking(false);
  };

  const inputStyle = {
    borderColor: 'rgba(26,42,26,0.14)',
    color: '#1A2A1A',
  };

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation
        onCartOpen={() => setIsCartOpen(true)}
        cartItemsCount={cartItemsCount}
      />

      {/* Hero */}
      <section className="pt-32 pb-12 md:pb-16 border-b" style={{ borderColor: 'rgba(26,42,26,0.08)', background: '#FAF9F6' }}>
        <div className="container mx-auto px-5 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="grid md:grid-cols-[1fr_360px] gap-10 md:items-end"
          >
            <div>
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Contact
              </p>
              <h1 className="text-5xl md:text-7xl font-light leading-tight mb-6" style={{ color: '#1A2A1A' }}>
                Support,<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>without friction.</span>
              </h1>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '46ch' }}>
                Send a message, track an order, or find the fastest path to the right Berleto support answer.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-px border" style={{ borderColor: 'rgba(26,42,26,0.08)', background: 'rgba(26,42,26,0.08)' }}>
              {[
                { n: '24h', label: 'Response time' },
                { n: 'Free', label: 'USA shipping' },
                { n: '30-day', label: 'Guarantee' },
                { n: 'CA', label: 'Ships from' },
              ].map((item) => (
                <div key={item.label} className="px-5 py-6" style={{ background: '#FAF9F6' }}>
                  <p className="text-2xl font-light mb-1" style={{ color: '#1A2A1A' }}>{item.n}</p>
                  <p className="text-[10px] tracking-[0.16em] uppercase leading-tight" style={{ color: 'rgba(26,42,26,0.4)' }}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Workspace */}
      <section className="py-12 md:py-20" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-5 sm:px-6">
          <div className="mb-10 flex gap-3 overflow-x-auto scrollbar-hide">
            {[
              { key: 'contact', label: 'Contact Us', icon: Send },
              { key: 'tracking', label: 'Track Order', icon: Package },
            ].map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className="inline-flex flex-none items-center gap-2 border px-5 py-3 text-xs font-medium tracking-[0.18em] uppercase transition-colors"
                  style={{
                    borderColor: active ? '#1A2A1A' : 'rgba(26,42,26,0.12)',
                    background: active ? '#1A2A1A' : 'transparent',
                    color: active ? '#FAF9F6' : 'rgba(26,42,26,0.62)',
                  }}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-[1fr_360px] gap-12 lg:gap-16 items-start">
            <AnimatePresence mode="wait">
              {activeTab === 'contact' ? (
                <motion.div
                  key="contact"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="border"
                  style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <div className="border-b px-5 py-6 md:px-8" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                    <p className="text-xs font-medium tracking-[0.25em] uppercase mb-3" style={{ color: '#B8704D' }}>
                      Message
                    </p>
                    <h2 className="text-3xl md:text-4xl font-light" style={{ color: '#1A2A1A' }}>
                      Tell us what you need.
                    </h2>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6 p-5 md:p-8">
                    <div className="grid md:grid-cols-2 gap-5">
                      <Field label="Name">
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className={fieldClass}
                          style={inputStyle}
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className={fieldClass}
                          style={inputStyle}
                        />
                      </Field>
                    </div>

                    <Field label="Subject">
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className={fieldClass}
                        style={inputStyle}
                      />
                    </Field>

                    <Field label="Message">
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={7}
                        className={`${fieldClass} resize-none`}
                        style={inputStyle}
                      />
                    </Field>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex w-full items-center justify-center gap-2 px-8 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60 md:w-auto"
                      style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                      onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#243424'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#1A2A1A'; }}
                    >
                      <Send className="h-4 w-4" />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="border p-4 text-sm" style={{ borderColor: 'rgba(74,124,89,0.24)', color: '#2F6F45', background: 'rgba(74,124,89,0.08)' }}>
                        Thank you for your message. We will get back to you within 24 hours.
                      </div>
                    )}
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="tracking"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="border"
                  style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <div className="border-b px-5 py-6 md:px-8" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                    <p className="text-xs font-medium tracking-[0.25em] uppercase mb-3" style={{ color: '#B8704D' }}>
                      Tracking
                    </p>
                    <h2 className="text-3xl md:text-4xl font-light mb-3" style={{ color: '#1A2A1A' }}>
                      Follow your order.
                    </h2>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '52ch' }}>
                      Enter your Berleto tracking number to see the latest shipment update.
                    </p>
                  </div>

                  <div className="p-5 md:p-8">
                    <form onSubmit={handleTrackingSubmit} className="mb-8 grid gap-4 md:grid-cols-[1fr_auto]">
                      <input
                        type="text"
                        placeholder="BRL-123456789"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        className={fieldClass}
                        style={inputStyle}
                        required
                      />
                      <button
                        type="submit"
                        disabled={isTracking}
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
                        style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                        onMouseEnter={(e) => { if (!isTracking) e.currentTarget.style.background = '#243424'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#1A2A1A'; }}
                      >
                        <Search className="h-4 w-4" />
                        {isTracking ? 'Tracking...' : 'Track'}
                      </button>
                    </form>

                    {trackingError && (
                      <div className="mb-8 flex gap-3 border p-4" style={{ borderColor: 'rgba(185,28,28,0.2)', color: '#991B1B', background: 'rgba(185,28,28,0.06)' }}>
                        <AlertCircle className="h-5 w-5 flex-none" />
                        <div>
                          <p className="text-sm font-medium">Tracking not found</p>
                          <p className="mt-1 text-sm opacity-80">{trackingError}</p>
                        </div>
                      </div>
                    )}

                    {trackingResult ? (
                      <div className="space-y-8">
                        <div className="border" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                          <div className="grid gap-px md:grid-cols-3" style={{ background: 'rgba(26,42,26,0.08)' }}>
                            {[
                              { label: 'Tracking number', value: trackingResult.number },
                              { label: 'Status', value: trackingResult.status },
                              { label: 'Estimated delivery', value: trackingResult.estimatedDelivery },
                            ].map((item) => (
                              <div key={item.label} className="p-5" style={{ background: '#FAF9F6' }}>
                                <p className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{ color: 'rgba(26,42,26,0.4)' }}>
                                  {item.label}
                                </p>
                                <p className="text-sm font-medium" style={{ color: '#1A2A1A' }}>{item.value}</p>
                              </div>
                            ))}
                          </div>
                          <div className="border-t p-5" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                            <p className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{ color: 'rgba(26,42,26,0.4)' }}>
                              Last update
                            </p>
                            <p className="text-sm font-medium" style={{ color: '#1A2A1A' }}>{trackingResult.lastUpdate}</p>
                            <p className="mt-1 text-sm" style={{ color: 'rgba(26,42,26,0.52)' }}>{trackingResult.location}</p>
                          </div>
                        </div>

                        <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                          {trackingResult.updates.map((update, index) => {
                            const isCurrent = index === 0;

                            return (
                              <div key={index} className="flex gap-4 border-b py-5" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                                <div className="pt-1">
                                  {isCurrent ? (
                                    <CheckCircle className="h-4 w-4" style={{ color: '#B8704D' }} />
                                  ) : (
                                    <Truck className="h-4 w-4" style={{ color: 'rgba(26,42,26,0.28)' }} />
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs mb-1" style={{ color: 'rgba(26,42,26,0.42)' }}>{update.date}</p>
                                  <p className="text-sm" style={{ color: '#1A2A1A' }}>{update.status}</p>
                                  {update.location && (
                                    <p className="mt-1 text-xs" style={{ color: 'rgba(26,42,26,0.46)' }}>{update.location}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="border p-5" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                        <p className="text-xs tracking-[0.18em] uppercase mb-4" style={{ color: 'rgba(26,42,26,0.4)' }}>
                          Test numbers
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {['BRL-123456789', 'BRL-987654321'].map((number) => (
                            <button
                              key={number}
                              type="button"
                              onClick={() => setTrackingNumber(number)}
                              className="font-mono text-sm transition-colors"
                              style={{ color: '#B8704D' }}
                            >
                              {number}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <aside className="lg:sticky lg:top-32">
              <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                {supportRows.map((row) => {
                  const Icon = row.icon;

                  return (
                    <div key={row.label} className="flex gap-4 border-b py-6" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                      <Icon className="mt-1 h-5 w-5 flex-none" style={{ color: '#B8704D' }} />
                      <div>
                        <p className="text-xs font-medium tracking-[0.18em] uppercase mb-2" style={{ color: 'rgba(26,42,26,0.4)' }}>
                          {row.label}
                        </p>
                        <p className="text-sm" style={{ color: '#1A2A1A' }}>{row.value}</p>
                        <p className="mt-1 text-sm" style={{ color: 'rgba(26,42,26,0.52)' }}>{row.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10">
                <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                  Common Questions
                </p>
                <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                  {quickLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      className="flex items-center justify-between border-b py-4 text-sm transition-colors"
                      style={{ borderColor: 'rgba(26,42,26,0.08)', color: 'rgba(26,42,26,0.62)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#B8704D'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(26,42,26,0.62)'; }}
                    >
                      {link.label}
                      <span style={{ color: '#B8704D' }}>→</span>
                    </a>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
